"use client";

import { useCallback, useState } from "react";
import Image from "next/image";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import illustrated from "./illustrated";
import photos from "./photos";
import useRecruiterDetails from "@/lib/user/useRecruiterDetails";
import { Avatar as UIAvatar } from "@/ui";

// A real asset rather than an inline data URI: the API only accepts avatar URLs
// that live under the client's /avatars/ paths, so a data URI was rejected on
// save. Served and validated exactly like the illustrated and photo tiles.
const GENERIC_AVATAR_PATH = "/avatars/generic/person.svg";

const TILE = 88;
const MAX_UPLOAD_SIZE = 2 * 1024 * 1024;

// Module-level so the lists are built once for the lifetime of the tab, not per
// mount. These are app-relative on purpose: next/image only optimises (and
// long-term caches) local paths, and an absolute same-origin URL would be
// treated as remote. The absolute form is derived at pick time instead.
const ILLUSTRATED_PATHS = illustrated.map(
  ({ file }) => `/avatars/illustrated/${file}`
);
const PHOTO_PATHS = photos.map(({ file }) => `/avatars/photos/${file}`);

function toAbsoluteUrl(path) {
  if (typeof window === "undefined") {
    return path;
  }
  return new URL(path, window.location.origin).href;
}

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dragging",
})(({ dragging, theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: 180,
  padding: theme.spacing(3),
  gap: theme.spacing(1),
  border: `1px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: dragging
    ? alpha(theme.palette.primary.main, 0.08)
    : "transparent",
  cursor: "pointer",
  textAlign: "center",
}));

function GridTile({ src, alt, onClick }) {
  return (
    <IconButton
      onClick={onClick}
      sx={{
        p: 0,
        width: TILE,
        height: TILE,
        borderRadius: "50%",
        transition: "box-shadow 120ms",
        "&:hover": {
          backgroundColor: "transparent",
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
        },
      }}
    >
      {/*
        next/image rather than a raw <img>: the source files are 100-160KB at
        full resolution and we render them at 88px. The optimiser resizes them
        and serves the result with a long-lived immutable Cache-Control, so the
        grid only pays the download cost once per browser.
      */}
      <Image
        src={src}
        alt={alt}
        width={TILE}
        height={TILE}
        sizes={`${TILE}px`}
        style={{ borderRadius: "50%", objectFit: "cover" }}
      />
    </IconButton>
  );
}

function PlaceholderTile({ label, onClick, disabled = false, children }) {
  return (
    <Stack
      spacing={1}
      alignItems="center"
      onClick={disabled ? undefined : onClick}
      sx={{ cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1 }}
    >
      <IconButton
        disableRipple={disabled}
        sx={{
          p: 0,
          width: TILE,
          height: TILE,
          borderRadius: "50%",
          transition: "box-shadow 120ms",
          "&:hover": {
            backgroundColor: "transparent",
            boxShadow: (theme) =>
              disabled ? "none" : `0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        {children}
      </IconButton>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} [props.onClose]
 * @param {(selection: { url: string, source: string }) => void} [props.onSelect]
 * @param {(file: File) => void} [props.onUpload]
 * @param {() => void} [props.onUseProfileAvatar] Offers "reset to the original
 *   profile photo" — only meaningful where a local copy can drift from the
 *   candidate record, i.e. the float/submission builders.
 * @param {string} [props.name] Used for the initials tile on the No Avatar tab.
 */
export default function AvatarLibrary({
  open,
  onClose,
  onSelect,
  onUpload,
  onUseProfileAvatar,
  name,
}) {
  const [tab, setTab] = useState("upload");
  const { recruiter } = useRecruiterDetails();
  const logoUrl = recruiter?.logo_url || null;

  const handlePick = (url, source) => {
    onSelect?.({ url, source });
    onClose?.();
  };

  // Library tiles render from an app-relative path but must be persisted as an
  // absolute URL — the API host allowlist rejects relative values.
  const handlePickAsset = (path, source) => handlePick(toAbsoluteUrl(path), source);

  const handleDropAccepted = useCallback(
    async (files) => {
      const file = files[0];
      if (!file) {
        return;
      }
      if (onUpload) {
        onUpload(file);
      } else {
        const dataUri = await readAsDataUrl(file);
        onSelect?.({ url: dataUri, source: "upload" });
      }
      onClose?.();
    },
    [onUpload, onSelect, onClose]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: handleDropAccepted,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/gif": [".gif"],
    },
    maxFiles: 1,
    maxSize: MAX_UPLOAD_SIZE,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: "calc(var(--z-modal, 1310) + 10)" }}
    >
      <DialogTitle sx={{ typography: "h2", pb: 1 }}>Choose an avatar</DialogTitle>
      <Typography variant="body2" sx={{ px: 3, pb: 3, color: "text.secondary" }}>
        Upload a photo, pick an illustrated or stock image, or use a placeholder instead.
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="upload" label="Upload" />
          <Tab value="illustrated" label="Illustrated" />
          <Tab value="photos" label="Photos" />
          <Tab value="no-avatar" label="No Avatar" />
        </Tabs>
      </Box>

      <DialogContent sx={{ minHeight: 360 }}>
        {tab === "upload" && (
          <Stack spacing={3}>
            <DropZone dragging={isDragActive} {...getRootProps()}>
              <input {...getInputProps()} />
              <Typography variant="body1">
                {isDragActive
                  ? "Drop the image here"
                  : "Drag and drop an image, or click to select"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG or GIF — up to 2MB
              </Typography>
            </DropZone>
          </Stack>
        )}

        {tab === "illustrated" && (
          <>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Friendly memoji-style illustrated avatars.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                gap: 2,
              }}
            >
              {ILLUSTRATED_PATHS.map((path) => (
                <GridTile
                  key={path}
                  src={path}
                  alt="Illustrated avatar"
                  onClick={() => handlePickAsset(path, "illustrated")}
                />
              ))}
            </Box>
          </>
        )}

        {tab === "photos" && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Recommended only for anonymous profiles where the candidate avatar
              will be blurred.
            </Alert>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                gap: 2,
              }}
            >
              {PHOTO_PATHS.map((path) => (
                <GridTile
                  key={path}
                  src={path}
                  alt="Photo avatar"
                  onClick={() => handlePickAsset(path, "photo")}
                />
              ))}
            </Box>
          </>
        )}

        {tab === "no-avatar" && (
          <Stack spacing={3} alignItems="center">
            <Typography variant="body2" sx={{ color: "text.secondary", alignSelf: "flex-start" }}>
              Use a placeholder instead of a photo.
            </Typography>
            <Stack direction="row" spacing={4} justifyContent="center" flexWrap="wrap">
              <PlaceholderTile
                label="Initials"
                onClick={() => handlePick(null, "initials")}
              >
                <UIAvatar name={name || "?"} size="xl" />
              </PlaceholderTile>
              <PlaceholderTile
                label="Generic icon"
                onClick={() => handlePickAsset(GENERIC_AVATAR_PATH, "generic")}
              >
                <Avatar
                  src={GENERIC_AVATAR_PATH}
                  alt=""
                  sx={{ width: TILE, height: TILE }}
                />
              </PlaceholderTile>
              {/*
                Shown even without a logo so the option is discoverable — the
                recruiter can go and add one in their profile settings.
              */}
              <PlaceholderTile
                label={logoUrl ? "Company logo" : "No company logo"}
                disabled={!logoUrl}
                onClick={() => handlePick(logoUrl, "company-logo")}
              >
                <Avatar
                  src={logoUrl || undefined}
                  alt=""
                  sx={{ width: TILE, height: TILE }}
                />
              </PlaceholderTile>
            </Stack>
            {onUseProfileAvatar && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  onUseProfileAvatar();
                  onClose?.();
                }}
              >
                Reset to original profile photo
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
